import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAuthContext } from '@/contexts/auth-context'
import { OnboardingWelcome } from '@/components/onboarding/onboarding-welcome'
import { LocationSelector, type Location } from '@/components/onboarding/location-selector'
import { OnboardingSuccess } from '@/components/onboarding/onboarding-success'
import {
  syncLocationsForAccount,
  batchUpdateLocationTracking,
  type Location as APILocation,
} from '@/lib/api/locations'
import { fetchUserAccounts } from '@/lib/api/accounts'
import { getApiUrl } from '@/lib/api/client'

type OnboardingStep = 'welcome' | 'select-locations' | 'success'

type DataSource = 'google' | 'savoria'

const mockLocations: Location[] = [
  {
    id: '1',
    name: 'The Italian Kitchen - Downtown',
    address: '123 Main Street, San Francisco, CA 94102',
    rating: 4.5,
    reviewCount: 234,
    imageUrl: undefined,
  },
  {
    id: '2',
    name: 'The Italian Kitchen - Marina',
    address: '456 Marina Blvd, San Francisco, CA 94123',
    rating: 4.2,
    reviewCount: 156,
    imageUrl: undefined,
  },
  {
    id: '3',
    name: 'The Italian Kitchen - Mission',
    address: '789 Valencia St, San Francisco, CA 94110',
    rating: 4.7,
    reviewCount: 312,
    imageUrl: undefined,
  },
  {
    id: '4',
    name: 'The Italian Kitchen - Oakland',
    address: '321 Broadway, Oakland, CA 94607',
    rating: 4.3,
    reviewCount: 89,
    imageUrl: undefined,
  },
]

export function OnboardingPage() {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  const { user } = useAuthContext()

  const [step, setStep] = useState<OnboardingStep>('welcome')
  const [locations, setLocations] = useState<Location[]>([])
  const [isLoadingLocations, setIsLoadingLocations] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [savedLocationCount, setSavedLocationCount] = useState(0)
  const [dataSource, setDataSource] = useState<DataSource>('google')

  useEffect(() => {
    const oauthSuccess = searchParams.get('oauth') === 'success'
    if (oauthSuccess) {
      setStep('select-locations')
      setDataSource('google')
      fetchLocationsAfterOAuth()
    }
  }, [searchParams])

  const fetchLocationsAfterOAuth = async () => {
    setIsLoadingLocations(true)
    try {
      console.log('[OAuth] User from auth context:', user?.email, user?.id)

      if (!user?.id) {
        throw new Error('User not authenticated')
      }

      // Fetch user's accounts (API uses JWT auth, so we just call without user_id param)
      console.log('[OAuth] Fetching accounts for user:', user.id)
      const accounts = await fetchUserAccounts(user.id)
      console.log('[OAuth] Fetched accounts:', accounts)

      if (accounts.length === 0) {
        console.warn('[OAuth] No accounts found for user')
        throw new Error(
          'No Google Business Profile accounts found. Please ensure your account has access to at least one location.'
        )
      }

      // Sync locations for each account
      const allLocations: APILocation[] = []
      for (const account of accounts) {
        console.log('[OAuth] Syncing locations for account:', account.id, account.account_name)
        const result = await syncLocationsForAccount(account.id)
        console.log('[OAuth] Synced locations:', result)
        allLocations.push(...result.locations)
      }

      console.log('[OAuth] Total locations fetched:', allLocations.length)

      if (allLocations.length === 0) {
        throw new Error('No locations found in your Google Business Profile accounts.')
      }

      // Convert backend Location format to component Location format
      const formattedLocations = allLocations.map((loc) => ({
        id: loc.id.toString(),
        name: loc.name,
        address: loc.address ?? '', // Provide empty string fallback for null
        rating: 0, // Backend doesn't provide this yet
        reviewCount: 0, // Backend doesn't provide this yet
        imageUrl: loc.website ?? undefined, // website URL can be used as fallback image
      }))

      setLocations(formattedLocations)
    } catch (error) {
      console.error('[OAuth] Failed to fetch locations:', error)
      console.error('[OAuth] Error details:', {
        message: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack : undefined,
      })

      // Fallback to mock data
      setLocations(mockLocations)

      const errorMessage =
        error instanceof Error ? error.message : t('onboarding.fetchLocationsFailed')
      alert(t('onboarding.loadLocationsError', { error: errorMessage }))
    } finally {
      setIsLoadingLocations(false)
    }
  }

  const handleConnectGoogle = () => {
    const redirectUri = `${window.location.origin}/auth/callback`
    const googleOAuthUrl = getApiUrl(
      `/auth/google/login?redirect_uri=${encodeURIComponent(redirectUri)}`
    )

    // Redirect to backend OAuth endpoint
    window.location.href = googleOAuthUrl
  }

  const handleSkipGoogle = () => {
    // Redirect to location search page for users who want to use Savorias' internal data
    navigate('/locations/search')
  }

  const handleSaveLocations = async (locationIds: string[]) => {
    setIsSaving(true)
    try {
      if (dataSource === 'google') {
        // Convert string IDs to numbers for backend
        const numericIds = locationIds.map((id) => parseInt(id, 10))

        // Enable tracking for selected locations
        await batchUpdateLocationTracking(numericIds, true)

        setSavedLocationCount(numericIds.length)
      } else {
        // Savorias mode - just save count (no backend call needed for mock data)
        setSavedLocationCount(locationIds.length)
      }

      setStep('success')
    } catch (error) {
      console.error('Failed to save locations:', error)
      alert(error instanceof Error ? error.message : t('onboarding.saveLocationsFailed'))
    } finally {
      setIsSaving(false)
    }
  }

  const handleGoToDashboard = () => {
    navigate('/dashboard')
  }

  switch (step) {
    case 'welcome':
      return <OnboardingWelcome onConnectGoogle={handleConnectGoogle} onSkip={handleSkipGoogle} />
    case 'select-locations':
      return (
        <LocationSelector
          locations={locations}
          isLoading={isLoadingLocations}
          onSaveLocations={handleSaveLocations}
          isSaving={isSaving}
          dataSource={dataSource}
        />
      )
    case 'success':
      return (
        <OnboardingSuccess
          locationCount={savedLocationCount}
          onGoToDashboard={handleGoToDashboard}
          dataSource={dataSource}
        />
      )
    default:
      return null
  }
}
